/*
 * CreatorApp — the SEPARATE creator surface (own layout, own sidebar, own
 * backend at /creator/*). Reached only via the Creator-mode switch; exits
 * cleanly back to the learner app. App theme tokens, not the builder's.
 * Tabs: Dashboard (homepage editor) · AI Interviews (groups + builder) · Blogs.
 */
import React, { useCallback, useEffect, useState } from 'react';
import '../../styles/creator.css';
import APIService from '../../services/APIService';
import { renderMarkdown } from '../nano/markdown';
import InterviewBuilder from '../nano/creator/InterviewBuilder';
import PackStudio from '../nano/creator/PackStudio';
import CreatorStorefront from '../nano/CreatorStorefront';

import { IconHome, IconMic, IconCreate, IconGraph } from '../nano/icons';

const TABS = [['dashboard', IconHome, 'Dashboard'], ['interviews', IconMic, 'AI Interviews'],
  ['packs', IconGraph, 'Study Material'], ['blogs', IconCreate, 'Blogs']];

// ── Dashboard: the creator's mentor profile — edit + view-as-user ───────────
const csv = (a) => (Array.isArray(a) ? a.join(', ') : a || '');
const lines = (a) => (Array.isArray(a) ? a.join('\n') : a || '');

const Dashboard = ({ me, person, onSaved }) => {
  const [h, setH] = useState({
    headline: me.headline || '', current_role: me.current_role || '', company: me.company || '',
    years_experience: me.years_experience || '', domains: csv(me.domains), skills: csv(me.skills),
    about_md: me.about_md || '', availability: me.availability || '',
    linkedin: me.linkedin || '', github: me.github || '', website: me.website || '',
    mentees: me.mentees || '', sessions: me.sessions || '', experience: lines(me.experience),
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [preview, setPreview] = useState(true);   // view-as-user is the default
  const set = (k) => (e) => setH({ ...h, [k]: e.target.value });

  const payload = () => ({
    ...h,
    years_experience: parseInt(h.years_experience, 10) || 0,
    mentees: parseInt(h.mentees, 10) || 0,
    sessions: parseInt(h.sessions, 10) || 0,
    domains: String(h.domains).split(',').map((x) => x.trim()).filter(Boolean),
    skills: String(h.skills).split(',').map((x) => x.trim()).filter(Boolean),
    experience: String(h.experience).split('\n').map((x) => x.trim()).filter(Boolean),
  });
  const save = async () => {
    setSaving(true);
    try { const p2 = payload(); await APIService.creatorSaveHomepage(p2); setSavedAt(Date.now()); onSaved(p2); }
    finally { setSaving(false); }
  };

  if (preview) {
    return (
      <div className="ca-previewwrap">
        <div className="ca-preview-bar">
          <span>This is your profile exactly as users see it (last saved version).</span>
          <button className="ca-primary" type="button" onClick={() => setPreview(false)}>✏ Back to editing</button>
        </div>
        <CreatorStorefront creatorId={me.creator_id} person={person}
          onBack={() => setPreview(false)} backLabel="← Back to editing" />
      </div>
    );
  }

  return (
    <div className="ca-panel">
      <div className="ca-panel-row">
        <div className="ca-panel-h">Your mentor profile</div>
        <button className="ca-ghost" type="button" onClick={async () => { await save(); setPreview(true); }}>
          View as user →
        </button>
      </div>
      <p className="ca-hint">This is your public page — what learners see from the Community and the landing page. Built for SDE / AI-ML mentoring: role, experience, domains, and your services (published AI interviews + posts).</p>

      <div className="ca-grid2">
        <label className="ca-field"><span>Current role</span>
          <input value={h.current_role} placeholder="e.g. Senior ML Engineer" onChange={set('current_role')} />
        </label>
        <label className="ca-field"><span>Company</span>
          <input value={h.company} placeholder="e.g. Google" onChange={set('company')} />
        </label>
      </div>
      <div className="ca-grid2">
        <label className="ca-field"><span>Years of experience</span>
          <input type="number" min="0" value={h.years_experience} placeholder="8" onChange={set('years_experience')} />
        </label>
        <label className="ca-field"><span>Headline</span>
          <input value={h.headline} placeholder="e.g. 200+ mock interviews · offers at FAANG" onChange={set('headline')} />
        </label>
      </div>
      <label className="ca-field"><span>Domains <em>comma-separated, shown as chips</em></span>
        <input value={h.domains} placeholder="AI/ML, System Design, DSA" onChange={set('domains')} />
      </label>
      <label className="ca-field"><span>Skills <em>comma-separated</em></span>
        <input value={h.skills} placeholder="PyTorch, LLMs, Distributed Systems, Leadership" onChange={set('skills')} />
      </label>
      <label className="ca-field"><span>About <em>markdown</em></span>
        <textarea value={h.about_md} placeholder={'## Hi, I\'m …\nI coach system design and ML interviews.'} onChange={set('about_md')} />
      </label>
      <label className="ca-field"><span>Experience <em>one line per role</em></span>
        <textarea value={h.experience} placeholder={'Senior ML Engineer @ Google · 2021–now\nSWE @ Flipkart · 2017–2021'} onChange={set('experience')} />
      </label>
      <div className="ca-grid2">
        <label className="ca-field"><span>Mentees helped</span>
          <input type="number" min="0" value={h.mentees} placeholder="120" onChange={set('mentees')} />
        </label>
        <label className="ca-field"><span>Sessions taken</span>
          <input type="number" min="0" value={h.sessions} placeholder="400" onChange={set('sessions')} />
        </label>
      </div>
      <div className="ca-grid2">
        <label className="ca-field"><span>LinkedIn URL</span>
          <input value={h.linkedin} placeholder="https://linkedin.com/in/…" onChange={set('linkedin')} />
        </label>
        <label className="ca-field"><span>GitHub</span>
          <input value={h.github} placeholder="username or URL" onChange={set('github')} />
        </label>
      </div>
      <div className="ca-grid2">
        <label className="ca-field"><span>Website</span>
          <input value={h.website} placeholder="https://…" onChange={set('website')} />
        </label>
        <label className="ca-field"><span>Availability</span>
          <input value={h.availability} placeholder="e.g. Weekends 10:00–18:00 IST · 48h notice" onChange={set('availability')} />
        </label>
      </div>

      <div className="ca-foot">
        {savedAt > 0 && Date.now() - savedAt < 4000 && <span className="ca-saved">✓ Saved</span>}
        <button className="ca-primary" type="button" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save profile'}</button>
      </div>
      <div className="ca-stats">
        <div><b>{me.counts?.interviews ?? 0}</b><span>AI interviews</span></div>
        <div><b>{me.counts?.packs ?? 0}</b><span>study packs</span></div>
        <div><b>{me.counts?.blogs ?? 0}</b><span>blogs</span></div>
        <div><b>{me.counts?.groups ?? 0}</b><span>groups</span></div>
      </div>
    </div>
  );
};

// ── AI Interviews: groups + list + builder ───────────────────────────────────
const Interviews = () => {
  const [groups, setGroups] = useState([]);
  const [interviews, setInterviews] = useState(null);
  const [newGroup, setNewGroup] = useState('');
  const [building, setBuilding] = useState(false);
  const [editing, setEditing] = useState(null);   // {id, title, config} — builder edits in place
  const [error, setError] = useState('');

  const load = useCallback(() => {
    APIService.creatorGroups().then((d) => setGroups(d.groups || [])).catch(() => {});
    APIService.creatorInterviews().then((d) => setInterviews(d.interviews || [])).catch((e) => { setError(e.message); setInterviews([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const addGroup = async () => {
    if (!newGroup.trim()) return;
    await APIService.creatorCreateGroup({ name: newGroup.trim() });
    setNewGroup('');
    load();
  };
  const removeGroup = async (g) => {
    if (!window.confirm(`Delete topic "${g.name}"? Its interviews stay, without a topic.`)) return;
    await APIService.creatorDeleteGroup(g.id);
    load();
  };
  const setGroupOf = async (ivw, group_id) => {
    await APIService.creatorPatchInterview(ivw.id, { group_id });
    load();
  };
  const toggleStatus = async (ivw) => {
    await APIService.creatorPatchInterview(ivw.id, { status: ivw.status === 'published' ? 'draft' : 'published' });
    load();
  };
  const remove = async (ivw) => {
    if (!window.confirm(`Delete "${ivw.title}"?`)) return;
    await APIService.creatorDeleteInterview(ivw.id);
    load();
  };

  const syncTopic = async (interviewId, envelope) => {
    const topic = (envelope.meta?.topic || '').trim();
    if (!topic) return;
    let g = groups.find((x) => x.name.toLowerCase() === topic.toLowerCase());
    if (!g) {
      g = await APIService.creatorCreateGroup({ name: topic });
      APIService.creatorGroups().then((d) => setGroups(d.groups || [])).catch(() => {});
    }
    if (g?.id) await APIService.creatorPatchInterview(interviewId, { group_id: g.id });
  };

  if (building) {
    return (
      <InterviewBuilder
        initial={editing ? { ...(editing.config || {}), title: editing.title } : null}
        onExit={() => { setBuilding(false); setEditing(null); load(); }}
        onPublish={async (envelope) => {
          const status = envelope.status || 'published';
          try {
            if (editing?.id) {
              await APIService.creatorPatchInterview(editing.id, { title: envelope.title, config: envelope, status });
              await syncTopic(editing.id, envelope);
            } else {
              const r = await APIService.creatorCreateInterview({ title: envelope.title, config: envelope, status });
              if (r?.id) {
                setEditing({ id: r.id, title: envelope.title, config: envelope });   // later saves update, not duplicate
                await syncTopic(r.id, envelope);
              }
            }
            load();
          } catch (e) { setError(e.message); }
        }}
      />
    );
  }

  return (
    <div className="ca-panel">
      <div className="ca-panel-row">
        <div className="ca-panel-h">AI Interviews</div>
        <button className="ca-primary" type="button" onClick={() => setBuilding(true)}>＋ Create AI interview</button>
      </div>
      {error && <div className="ca-error">{error}</div>}

      <div className="ca-groups">
        <span className="ca-groups-h">Topics</span>
        {groups.map((g) => (
          <span key={g.id} className="ca-group">
            {g.name}
            <button type="button" aria-label={`Delete topic ${g.name}`} onClick={() => removeGroup(g)}>✕</button>
          </span>
        ))}
        <span className="ca-group new">
          <input value={newGroup} placeholder="Add a topic — e.g. System Design"
            onChange={(e) => setNewGroup(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addGroup(); }} />
          <button type="button" onClick={addGroup} disabled={!newGroup.trim()}>Add</button>
        </span>
      </div>

      {interviews === null ? <div className="ca-hint">Loading…</div>
        : interviews.length === 0 ? (
          <div className="ca-empty">No interviews yet. <b>＋ Create AI interview</b> opens the builder — design the questions, rubric, and persona; publishing lists it on your homepage.</div>
        ) : (
          <div className="ca-list">
            {interviews.map((ivw) => (
              <div key={ivw.id} className="ca-row">
                <button type="button" className="ca-row-main link"
                  title="Edit this interview"
                  onClick={async () => {
                    try {
                      const doc = await APIService.creatorGetInterview(ivw.id);
                      setEditing(doc); setBuilding(true);
                    } catch (e) { setError(e.message); }
                  }}>
                  <span className="ca-row-t">{ivw.title}</span>
                  <span className="ca-row-m">{ivw.format} · updated {ivw.updated_at?.slice(0, 10)} · click to edit</span>
                </button>
                <select className="ca-select" value={ivw.group_id || ''} onChange={(e) => setGroupOf(ivw, e.target.value)}>
                  <option value="">No topic</option>
                  {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
                <button type="button" className={`ca-status ${ivw.status}`} onClick={() => toggleStatus(ivw)}
                  title="Click to toggle">
                  {ivw.status === 'published' ? '● Published' : '○ Draft'}
                </button>
                <button type="button" className="ca-del" onClick={() => remove(ivw)} aria-label="Delete">🗑</button>
              </div>
            ))}
          </div>
        )}

    </div>
  );
};

// ── Blogs: markdown list + editor ────────────────────────────────────────────
const Blogs = () => {
  const [blogs, setBlogs] = useState(null);
  const [editing, setEditing] = useState(null);   // {id?, title, markdown}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    APIService.creatorBlogs().then((d) => setBlogs(d.blogs || [])).catch(() => setBlogs([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = async (b) => setEditing(await APIService.creatorGetBlog(b.id));
  const save = async () => {
    if (!editing.title?.trim()) return;
    setBusy(true);
    try {
      if (editing.id) await APIService.creatorUpdateBlog(editing.id, { title: editing.title, markdown: editing.markdown || '', status: 'published' });
      else await APIService.creatorCreateBlog({ title: editing.title, markdown: editing.markdown || '' });
      setEditing(null);
      load();
    } finally { setBusy(false); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    await APIService.creatorDeleteBlog(b.id);
    load();
  };

  return (
    <div className="ca-panel">
      <div className="ca-panel-row">
        <div className="ca-panel-h">Blogs</div>
        <button className="ca-primary" type="button" onClick={() => setEditing({ title: '', markdown: '# \n\n' })}>＋ New blog</button>
      </div>
      <p className="ca-hint">Markdown posts, free to read on your homepage. Paid notes & guides come later.</p>
      {editing ? (
        <div className="ca-editor">
          <input className="ca-ed-title" value={editing.title} placeholder="Blog title"
            onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
          <div className="ca-ed-split">
            <textarea value={editing.markdown || ''} spellCheck={false}
              onChange={(e) => setEditing({ ...editing, markdown: e.target.value })} />
            <div className="ca-ed-preview">{renderMarkdown(editing.markdown || '')}</div>
          </div>
          <div className="ca-foot">
            <button className="ca-ghost" type="button" onClick={() => setEditing(null)}>Cancel</button>
            <button className="ca-primary" type="button" onClick={save} disabled={busy || !editing.title.trim()}>
              {busy ? 'Saving…' : 'Publish'}
            </button>
          </div>
        </div>
      ) : blogs === null ? <div className="ca-hint">Loading…</div>
        : blogs.length === 0 ? <div className="ca-empty">No posts yet.</div>
          : (
            <div className="ca-list">
              {blogs.map((b) => (
                <div key={b.id} className="ca-row">
                  <button type="button" className="ca-row-main link" onClick={() => open(b)}>
                    <span className="ca-row-t">{b.title}</span>
                    <span className="ca-row-m">updated {b.updated_at?.slice(0, 10)}</span>
                  </button>
                  <button type="button" className="ca-del" onClick={() => remove(b)} aria-label="Delete">🗑</button>
                </div>
              ))}
            </div>
          )}
    </div>
  );
};

// ── The creator shell ────────────────────────────────────────────────────────
export default function CreatorApp({ onExit, userName }) {
  const [me, setMe] = useState(null);          // null=loading, {enabled:false} = gate
  const [tab, setTab] = useState('dashboard');
  const [navEpoch, setNavEpoch] = useState(0);   // sidebar clicks remount tabs — exits the builder

  const [person, setPerson] = useState(null);
  useEffect(() => {
    APIService.creatorMe().then(setMe).catch(() => setMe({ enabled: false, error: true }));
    APIService.getProfile().then(setPerson).catch(() => {});
  }, []);

  const request = async () => {
    await APIService.creatorEnable();
    setMe(await APIService.creatorMe());
  };

  return (
    <div className="nano-root ca-root" style={{ '--mx': '#c084fc' }}>
      <div className="nano-glow" aria-hidden="true" />
      <aside className="ca-side">
        <div className="ca-brand">✦ DataVerse.ai <span>Studio</span></div>
        {TABS.map(([id, Ic, label]) => (
          <button key={id} type="button" className={`ca-nav${tab === id ? ' on' : ''}`}
            onClick={() => { setTab(id); setNavEpoch((e) => e + 1); }} disabled={!me?.enabled}>
            <span className="ca-nav-ic"><Ic size={16} /></span>{label}
          </button>
        ))}
        <div className="ca-side-spacer" />
        <button type="button" className="ca-exit" onClick={onExit}>← Back to learner mode</button>
      </aside>

      <main className="ca-main">
        {me === null ? (
          <div className="ca-center">Checking your creator access…</div>
        ) : !me.enabled ? (
          <div className="ca-center gate">
            <h1>Become a creator</h1>
            {me.status === 'pending' ? (
              <p>Your request is <b>pending admin approval</b>. You'll get access here as soon as it's granted.</p>
            ) : me.status === 'rejected' ? (
              <p>Your creator request was <b>not approved</b>. Contact the team if you think this is a mistake.</p>
            ) : me.status === 'disabled' ? (
              <p>Your creator access is currently <b>disabled</b> by an admin.</p>
            ) : (
              <>
                <p>Publish AI-run mock interviews, write blogs, and open your own coaching homepage{userName ? `, ${userName}` : ''}. Access is granted by an admin after you request it.</p>
                <button className="ca-primary big" type="button" onClick={request}>Request creator access →</button>
              </>
            )}
          </div>
        ) : tab === 'dashboard' ? <Dashboard me={me} person={person} onSaved={(h) => setMe({ ...me, ...h })} />
          : tab === 'interviews' ? <Interviews key={navEpoch} />
            : tab === 'packs' ? <PackStudio key={navEpoch} />
              : <Blogs />}
      </main>
    </div>
  );
}
